import { AccountDetailsFormValues, EarnedBadge, MyDetail } from '@account/types';
import { Distributor } from '@authorizedInstaller/types/authorizedInstallers';
import { ICountry } from '@common/types/country';
import { Filters, Pagination, PaginationProps, PaginationReturn } from '@common/types/service';
import {
    ILoginResponse,
    ISetNewPasswordData,
    ISignUpAPIFormValues,
    IUser,
    ManagementAccountCertificate,
    ManagementAccountDetail,
    PublicUserInfo,
    Roles,
    UserAccountData,
    UserAccountResponse,
    UserList,
} from '@common/types/user';
import { GUEST_ACCESS_HASH_KEY } from '@event/constants';
import { EventTypes, ManagementAnyEvent, SalesManager } from '@event/types/event';
import { api } from '@services/api';
import { getFilterString, getPaginationString, isPaginationReturn, serializeQueryParams } from '@utils/service';

type AccountListReturn = Omit<PaginationReturn<UserList>, 'queryset_ids'>;
type AccountListWithQuerysetIDsReturn = PaginationReturn<UserList>;

type GetAccountsParams = Pagination & {
    search?: string;
    role?: Roles[];
    sales_manager?: number[];
    country?: string[];
};

type GetAccountsWithQuerysetIDsParams = GetAccountsParams & {
    include_queryset_ids: true;
};

const accountsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getSalesManagers: builder.query<SalesManager[], Filters | void>({
            query: (filters) => `accounts/sales-manager${filters ? `?${getFilterString(filters)}` : ''}`,
            providesTags: ['SalesManagers'],
        }),
        getUsersRoles: builder.query<Roles[], { user_ids: number[] }>({
            query: ({ user_ids }) =>
                'management/accounts/roles/?' +
                getFilterString({
                    id: user_ids,
                }),
            providesTags: ['UsersRoles'],
            transformResponse: (response: { role: Roles }[]) => response.map((x) => x.role),
        }),
        login: builder.mutation<ILoginResponse, { email: string; password: string }>({
            query: (body) => ({
                url: 'public/accounts/login/',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['User'],
        }),
        signUp: builder.mutation<
            Omit<ISignUpAPIFormValues, 'password'> & { sales_manager: number; distributor: number },
            ISignUpAPIFormValues
        >({
            query: (body) => ({
                url: 'public/accounts/sign-up/',
                method: 'POST',
                body,
            }),
        }),
        forgotPassword: builder.mutation<void, { email: string }>({
            query: (body) => ({
                url: 'public/accounts/forgot-password/',
                method: 'POST',
                body,
            }),
        }),
        magicLink: builder.mutation<void, { email: string }>({
            query: (body) => ({
                url: 'public/accounts/magic-link/',
                method: 'POST',
                body,
            }),
        }),
        reactivationRequest: builder.mutation<void, { email: string }>({
            query: (body) => ({
                url: 'public/accounts/reactivation-request/',
                method: 'POST',
                body,
            }),
        }),
        patchSelf: builder.mutation<UserAccountResponse, UserAccountData>({
            query: (body) => ({
                url: 'accounts/me',
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['Users'],
        }),
        patchFoodPreference: builder.mutation<
            { food_preference: IUser['food_preference'] },
            { food_preference: IUser['food_preference']; guest_access_hash?: string | null }
        >({
            query: ({ guest_access_hash, ...body }) => ({
                url: 'accounts/me/food-preference/',
                method: 'PATCH',
                params: { guest_access_hash },
                body,
            }),
            invalidatesTags: ['Users'],
        }),
        getDistributors: builder.query<Distributor[], Filters | void>({
            query: (filters) => `accounts/distributor${filters ? `?${getFilterString(filters)}` : ''}`,
            providesTags: ['Distributors'],
        }),
        getUserByGuestHash: builder.query<PublicUserInfo, { guest_access_hash: string }>({
            query: ({ guest_access_hash }) => `public/accounts/me?${GUEST_ACCESS_HASH_KEY}=${guest_access_hash}`,
            providesTags: ['GuestUser'],
        }),
        getMyEvents: builder.query<PaginationReturn<ManagementAnyEvent> | ManagementAnyEvent[], PaginationProps>({
            query: ({ filters, ...pagination }) =>
                `management/accounts/me/events/?${getPaginationString(pagination)}${
                    filters ? getFilterString(filters) : ''
                }`,
            providesTags: ['MyEvents'],
            transformResponse: (
                response:
                    | (ManagementAnyEvent & { possible_subscribers?: string })[]
                    | PaginationReturn<ManagementAnyEvent & { possible_subscribers?: string }>
            ) =>
                isPaginationReturn(response)
                    ? {
                          ...response,
                          results: response?.results.map((event) => {
                              const transformedEvent = {
                                  ...event,
                                  type: event.type.replace('_', '-') as EventTypes,
                                  possible_subscribers: event.possible_subscribers
                                      ? (event.possible_subscribers.split(',') as Roles[])
                                      : [],
                              };
                              return transformedEvent;
                          }),
                      }
                    : response?.map((event) => {
                          const transformedEvent = {
                              ...event,
                              type: event.type.replace('_', '-') as EventTypes,
                              possible_subscribers: event.possible_subscribers
                                  ? (event.possible_subscribers.split(',') as Roles[])
                                  : [],
                          };
                          return transformedEvent;
                      }),
        }),
        allUsers: builder.query<PaginationReturn<IUser>, PaginationProps>({
            query: ({ filters, ...pagination }) =>
                `/management/accounts/?${getPaginationString(pagination)}${filters ? getFilterString(filters) : ''}`,
            providesTags: ['Users'],
        }),
        getEndUsers: builder.query<{ id: number; full_name: string }[], { search: string }>({
            query: ({ search }) => `management/accounts/end-user/${search ? `?search=${search}` : ''}`,
            providesTags: ['End User'],
        }),
        setNewUserPassword: builder.mutation<void, ISetNewPasswordData>({
            query: ({ password, password_hash }) => ({
                url: `public/accounts/reset-password/${password_hash}/`,
                method: 'POST',
                body: { password },
            }),
        }),
        getMyEarnedBadges: builder.query<EarnedBadge[] | PaginationReturn<EarnedBadge>, PaginationProps>({
            query: (params) => ({
                url: `accounts/me/level-badges/`,
                params,
            }),
            providesTags: ['MyEarnedBadges'],
        }),
        getMyDetails: builder.query<MyDetail, void>({
            query: () => 'accounts/me/details/',
            providesTags: ['UserDetails'],
        }),
        putMyDetails: builder.mutation<void, AccountDetailsFormValues>({
            query: (body) => ({
                url: 'accounts/me/details/',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Users', 'User', 'UserDetails'],
        }),
        updatePassword: builder.mutation<void, { current_password: string; new_password: string }>({
            query: (body) => ({
                url: 'accounts/update-password/',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Users', 'User', 'UserDetails'],
        }),
        getAccounts: builder.query<AccountListReturn, GetAccountsParams>({
            query: (params) => {
                const queryString = serializeQueryParams(params);

                return {
                    url: `management/accounts/${queryString ? `?${queryString}` : ''}`,
                };
            },
            providesTags: (_res, _error, { page, page_size }) => [{ type: 'Accounts', id: `${page}-${page_size}` }],
        }),
        getAccountsWithQuerysetIDs: builder.query<AccountListWithQuerysetIDsReturn, GetAccountsWithQuerysetIDsParams>({
            query: (params) => {
                const queryString = serializeQueryParams(params);

                return {
                    url: `management/accounts/${queryString ? `?${queryString}` : ''}`,
                };
            },
            providesTags: (_res, _error, { page, page_size }) => [{ type: 'Accounts', id: `${page}-${page_size}` }],
        }),
        getAccountsByIDs: builder.query<PaginationReturn<UserList>, Pagination & { user_ids: number[] }>({
            query: ({ ordering, ...body }) => {
                const queryString = serializeQueryParams({ ordering });

                return {
                    url: `management/accounts/${queryString ? `?${queryString}` : ''}`,
                    method: 'POST',
                    body,
                };
            },
            providesTags: (_res, _error, { user_ids }) => [{ type: 'AccountsByIDs', id: user_ids.join(',') }],
        }),
        createMailchimpAudienceByIDs: builder.mutation<void, { user_ids: number[]; audience_name: string }>({
            query: (body) => ({
                url: 'management/accounts/mailchimp-audience/selected/',
                method: 'POST',
                body,
            }),
        }),
        createMailchimpAudienceByFilter: builder.mutation<
            void,
            {
                params: {
                    search?: string;
                    role?: Roles[];
                    sales_manager?: number[];
                    country?: string[];
                };
                audience_name: string;
            }
        >({
            query: ({ params, ...body }) => ({
                url: 'management/accounts/mailchimp-audience/',
                params,
                method: 'POST',
                body,
            }),
        }),
        deleteAccount: builder.mutation<void, { user_id: number }>({
            query: ({ user_id }) => ({
                url: `management/accounts/${user_id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Accounts', 'AccountsByIDs'],
        }),
        getAccountByID: builder.query<ManagementAccountDetail, { user_id: number }>({
            query: ({ user_id }) => ({
                url: `management/accounts/${user_id}/`,
            }),
            providesTags: (_res, _error, { user_id }) => [{ type: 'AccountDetails', id: user_id.toString() }],
        }),
        patchAccount: builder.mutation<
            void,
            { user_id: number; data: Partial<Omit<ManagementAccountDetail, 'id' | 'field_permissions'>> }
        >({
            query: ({ user_id, data }) => ({
                url: `management/accounts/${user_id}/`,
                method: 'PATCH',
                body: data,
            }),
            invalidatesTags: (_res, _error, { user_id }) => [
                { type: 'AccountDetails', id: user_id.toString() },
                'Accounts',
                'AccountsByIDs',
            ],
        }),
        getAccountCertificates: builder.query<
            PaginationReturn<ManagementAccountCertificate>,
            Pagination & { user_id: number }
        >({
            query: ({ user_id, ...params }) => ({
                url: `management/accounts/${user_id}/certificates/`,
                params,
            }),
            providesTags: (_res, _error, { user_id }) => [{ type: 'AccountCertificates', id: user_id.toString() }],
        }),
        getAccountBadges: builder.query<PaginationReturn<EarnedBadge>, Pagination & { user_id: number }>({
            query: ({ user_id, ...params }) => ({
                url: `management/accounts/${user_id}/level-badges/`,
                params,
            }),
            providesTags: (_res, _error, { user_id }) => [{ type: 'AccountBadges', id: user_id.toString() }],
        }),
        getManagedCountries: builder.query<ICountry[], void>({
            query: () => 'accounts/me/managed-countries/',
        }),
    }),
});

export const {
    useGetSalesManagersQuery,
    useLazyGetUsersRolesQuery,
    useLoginMutation,
    useSignUpMutation,
    useForgotPasswordMutation,
    useMagicLinkMutation,
    useReactivationRequestMutation,
    usePatchSelfMutation,
    usePatchFoodPreferenceMutation,
    useGetDistributorsQuery,
    useGetUserByGuestHashQuery,
    useLazyGetUserByGuestHashQuery,
    useGetUsersRolesQuery,
    useGetMyEventsQuery,
    useAllUsersQuery,
    useGetEndUsersQuery,
    useSetNewUserPasswordMutation,
    useGetMyEarnedBadgesQuery,
    useLazyGetMyEarnedBadgesQuery,
    usePutMyDetailsMutation,
    useGetMyDetailsQuery,
    useUpdatePasswordMutation,
    useGetAccountsQuery,
    useLazyGetAccountsWithQuerysetIDsQuery,
    useGetAccountsByIDsQuery,
    useCreateMailchimpAudienceByIDsMutation,
    useCreateMailchimpAudienceByFilterMutation,
    useDeleteAccountMutation,
    useGetAccountByIDQuery,
    usePatchAccountMutation,
    useLazyGetAccountCertificatesQuery,
    useLazyGetAccountBadgesQuery,
    useGetManagedCountriesQuery,
} = accountsApi;
export default accountsApi;
